import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  IWHCheckDefinitionDTO,
  IWHChecklistSectionWithCheckDTO,
  IWHFlowMetadataTranslationModel,
  WHTranslationTypeENUM,
} from '@workheld/workheld-shared-lib';
import { WHEnumDataService } from 'src/app/app-services-helper/w-h-enum-helper.service';
import { WHButtonComponent } from 'src/app/app-shared/components/buttons/w-h-button/w-h-button.component';
import { WHPipesModule } from 'src/app/app-shared/shared-pipes/wh-pipes.module';
import { ChecklistStickyHeaderDirective } from '../checklist-sticky-header.directive';

interface ChecklistTableRow {
  trackId: string;
  position: number;
  description: string;
  additionalInformation: string | null;
  measurementTypeKey: string | null;
  resultTypeId: number | null;
  unitKey: string | null;
  mandatory: boolean;
}

interface ChecklistTableGroup {
  trackId: string;
  /** `null` for the checks that are not assigned to any section. */
  title: string | null;
  defaultEnabled: boolean;
  rows: ChecklistTableRow[];
}

const bySortPosition = (
  a: { sortPosition?: number },
  b: { sortPosition?: number },
): number => (a.sortPosition ?? 0) - (b.sortPosition ?? 0);

/**
 * Read-only checklist table: column headers once, sections as banner rows.
 */
@Component({
  selector: 'ng-bee-checklist-table',
  imports: [
    MatIconModule,
    MatSlideToggleModule,
    WHButtonComponent,
    WHPipesModule,
    ChecklistStickyHeaderDirective,
  ],
  templateUrl: './checklist-table.component.html',
  styleUrls: ['../checklist-table.scss', './checklist-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChecklistTableComponent {
  /** Checks that are not assigned to any section; shown before the sections. */
  public readonly checks = input<IWHCheckDefinitionDTO[] | null | undefined>(
    [],
  );
  public readonly sections = input<
    IWHChecklistSectionWithCheckDTO[] | null | undefined
  >([]);

  private readonly whEnumDataService = inject(WHEnumDataService);

  protected readonly groups = computed<ChecklistTableGroup[]>(() => {
    const enums = this.whEnumDataService.$ENUMS();
    const toRows = (checks: IWHCheckDefinitionDTO[] | null | undefined) =>
      [...(checks ?? [])]
        .sort(bySortPosition)
        .map((check, index) => toRow(check, index, enums));

    const sections = [...(this.sections() ?? [])].sort(bySortPosition);
    const unsectioned = toRows(this.checks());
    const groups: ChecklistTableGroup[] = sections.map((section, index) => ({
      trackId: section.id ?? `section-${index}`,
      title: section.title,
      defaultEnabled: !!section.defaultEnabled,
      rows: toRows(section.checkDefinitions),
    }));

    return unsectioned.length || !groups.length
      ? [
          {
            trackId: 'unsectioned',
            title: null,
            defaultEnabled: false,
            rows: unsectioned,
          },
          ...groups,
        ]
      : groups;
  });

  /** Shows long texts in full instead of clamping them to two lines. */
  protected readonly expandedView = signal(false);

  /** Track ids of collapsed sections. Sections start expanded. */
  protected readonly collapsedSections = signal<ReadonlySet<string>>(new Set());
  protected readonly sectionIds = computed(() =>
    this.groups()
      .filter((group) => group.title !== null)
      .map((group) => group.trackId),
  );
  protected readonly checkCount = computed(() =>
    this.groups().reduce((sum, group) => sum + group.rows.length, 0),
  );
  protected readonly allCollapsed = computed(
    () =>
      this.sectionIds().length > 0 &&
      this.sectionIds().every((id) => this.collapsedSections().has(id)),
  );

  protected toggleSection(trackId: string): void {
    this.collapsedSections.update((collapsed) => {
      const next = new Set(collapsed);
      if (!next.delete(trackId)) {
        next.add(trackId);
      }
      return next;
    });
  }

  protected toggleAllSections(): void {
    this.collapsedSections.set(
      this.allCollapsed() ? new Set() : new Set(this.sectionIds()),
    );
  }
}

function toRow(
  check: IWHCheckDefinitionDTO,
  index: number,
  enums: IWHFlowMetadataTranslationModel | undefined,
): ChecklistTableRow {
  return {
    trackId: check.id ?? `new-${index}`,
    position: (check.sortPosition ?? index) + 1,
    description: check.description,
    additionalInformation: check.additionalInformation?.trim() || null,
    measurementTypeKey: findTranslateKey(
      enums?.[WHTranslationTypeENUM.MEASUREMENT_TYPE],
      check.measurementType,
    ),
    resultTypeId: check.checkResultTypeId ?? null,
    unitKey: findTranslateKey(enums?.[WHTranslationTypeENUM.UNIT], check.unit),
    mandatory: !!check.mandatory,
  };
}

function findTranslateKey(
  options: { id: string; translateKey: string }[] | undefined,
  value: unknown,
): string | null {
  if (value == null || value === '') {
    return null;
  }
  return (
    options?.find((option) => option.id === String(value))?.translateKey ?? null
  );
}
